import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  PauseCircle,
  PlayCircle,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import PrivateLayout from '../../layouts/PrivateLayout';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import ConfirmModal from '../shared/ConfirmModal';
import { fmtDate } from '../shared/billingFormat';
import {
  useFetchBillingSettings,
  useUpdateBillingSettings,
  useFetchBillingSuppressions,
  useAddBillingSuppression,
  useRemoveBillingSuppression,
} from '../../../services/billingAdmin';

const offsetLabel = (days) => {
  if (days === 0) return 'On the due date';
  return days === 1 ? 'day after the due date' : 'days after the due date';
};

// ---------------------------------------------------------------------------

// Split settings row: section title + explanation in a left rail, the
// controls card on the right. Stacks on small screens.
const SettingsSection = ({ icon, title, description, children }) => (
  <div className="grid gap-4 py-8 last:pb-0 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-10">
    <div>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="mb-0 text-base font-semibold text-foreground">{title}</h2>
      </div>
      <p className="mb-0 mt-1.5 text-sm leading-relaxed text-muted-foreground">
        {description}
      </p>
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------

const MasterPauseCard = ({ settings, onToggle, isSaving }) => {
  const [confirming, setConfirming] = useState(false);
  const paused = settings?.remindersEnabled === false;

  return (
    <Card className={cn(paused && 'border-status-error bg-status-error-bg/30')}>
      <CardContent className="flex items-center justify-between gap-4 px-5 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2">
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full',
              paused ? 'bg-status-error-fg' : 'bg-status-success-fg',
            )}
          />
          <span className="text-sm font-semibold text-foreground">
            {paused ? 'Paused' : 'Running'}
          </span>
          <span className="text-sm text-muted-foreground">
            {paused
              ? 'No billing reminders will send to any client until this is turned back on.'
              : 'Sending on the cadence below.'}
          </span>
        </div>
        {paused ? (
          <Button size="sm" onClick={() => onToggle(true)} disabled={isSaving}>
            <PlayCircle className="mr-1.5 h-4 w-4" /> Resume reminders
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setConfirming(true)} disabled={isSaving}>
            <PauseCircle className="mr-1.5 h-4 w-4" /> Pause all reminders
          </Button>
        )}
      </CardContent>
      {confirming ? (
        <ConfirmModal
          title="Pause all reminders"
          body="This stops EVERY reminder email to every client until reminders are resumed here. Use it when something is wrong and you need billing email silent immediately."
          confirmLabel="Pause all reminders"
          destructive
          isSaving={isSaving}
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            await onToggle(false);
            setConfirming(false);
          }}
        />
      ) : null}
    </Card>
  );
};

// ---------------------------------------------------------------------------

const CadenceEditorCard = ({ settings, onSave, isSaving }) => {
  const saved = settings?.reminderCadence || { steps: [], recurringEveryDays: 14 };
  const [steps, setSteps] = useState(saved.steps);
  const [recurring, setRecurring] = useState(saved.recurringEveryDays);

  // Re-sync local edits when fresh settings arrive (e.g. after save).
  useEffect(() => {
    setSteps(saved.steps);
    setRecurring(saved.recurringEveryDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(saved)]);

  const dirty =
    JSON.stringify([...steps].sort((a, b) => a - b)) !== JSON.stringify(saved.steps) ||
    Number(recurring) !== saved.recurringEveryDays;

  const problems = useMemo(() => {
    const list = [];
    const nums = steps.map(Number);
    if (nums.length === 0) list.push('At least one step is required.');
    if (nums.some((n) => !Number.isInteger(n) || n < 0 || n > 365)) {
      list.push('Steps must be whole numbers between 0 and 365.');
    }
    const sorted = [...nums].sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i] === sorted[i - 1]) list.push('Steps must not repeat.');
      else if (sorted[i] - sorted[i - 1] < 3) list.push('Steps must be at least 3 days apart.');
    }
    const rec = Number(recurring);
    if (!Number.isInteger(rec) || (rec !== 0 && (rec < 7 || rec > 90))) {
      list.push('The repeat interval must be 0 (stop) or between 7 and 90 days.');
    }
    return [...new Set(list)];
  }, [steps, recurring]);

  const updateStep = (index, value) => {
    const next = [...steps];
    next[index] = value === '' ? '' : Number(value);
    setSteps(next);
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="divide-y divide-border rounded-lg border border-border">
          {steps.map((step, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="flex items-center gap-3 px-3 py-2">
              <Input
                type="number"
                min={0}
                max={365}
                value={step}
                onChange={(e) => updateStep(index, e.target.value)}
                onBlur={() => setSteps((s) => [...s].map(Number).sort((a, b) => a - b))}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">
                {Number.isInteger(Number(step)) ? offsetLabel(Number(step)) : ''}
              </span>
              <button
                type="button"
                onClick={() => setSteps(steps.filter((_, i) => i !== index))}
                className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-status-error-fg"
                aria-label="Remove step"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setSteps([...steps, (Math.max(...steps.map(Number), 0) || 0) + 15])}
            className="flex w-full items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-4 w-4" /> Add step
          </button>
        </div>

        <div className="flex items-center gap-2 border-t border-border pt-4 text-sm text-foreground">
          After the last step, keep reminding every
          <Input
            type="number"
            min={0}
            max={90}
            value={recurring}
            onChange={(e) => setRecurring(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-20"
          />
          days
          <span className="text-xs text-muted-foreground">(set to 0 to stop after the last step)</span>
        </div>

        {problems.length > 0 ? (
          <ul className="mb-0 list-disc pl-5 text-sm text-status-error-fg">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}

        {dirty ? (
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button
              variant="secondary"
              onClick={() => {
                setSteps(saved.steps);
                setRecurring(saved.recurringEveryDays);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              disabled={isSaving || problems.length > 0}
              onClick={() =>
                onSave({
                  reminderCadence: {
                    steps: [...steps].map(Number).sort((a, b) => a - b),
                    recurringEveryDays: Number(recurring),
                  },
                })
              }
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save cadence
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------

const ExclusionListCard = () => {
  const { data: suppressions, isLoading, isError, refetch } = useFetchBillingSuppressions();
  const addSuppression = useAddBillingSuppression();
  const removeSuppression = useRemoveBillingSuppression();

  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmingAdd, setConfirmingAdd] = useState(false);
  const [removing, setRemoving] = useState(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailValid && reason.trim().length > 0;

  const handleAdd = async () => {
    try {
      await addSuppression.mutateAsync({ email: email.trim(), reason: reason.trim() });
      toast.success('Address added to the exclusion list');
      setEmail('');
      setReason('');
      setConfirmingAdd(false);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not add the address');
    }
  };

  const handleRemove = async () => {
    try {
      await removeSuppression.mutateAsync(removing.suppressionId);
      toast.success('Address removed. Billing emails resume on the normal cadence.');
      setRemoving(null);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not remove the address');
    }
  };

  return (
    <Card className="border-status-error/40">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Email address
            </label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client@example.com"
            />
          </div>
          <div className="min-w-56 flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Reason (required)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this address blocked?"
            />
          </div>
          <Button
            variant="outline"
            className="border-status-error text-status-error-fg hover:bg-status-error-bg"
            disabled={!canSubmit || addSuppression.isLoading}
            onClick={() => setConfirmingAdd(true)}
          >
            Block address
          </Button>
        </div>

        {isError ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            Could not load the exclusion list.
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="py-2 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : (suppressions || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">No addresses are excluded.</div>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {suppressions.map((s) => (
              <div key={s.suppressionId} className="flex items-center justify-between gap-4 px-4 py-2.5">
                <div>
                  <div className="text-sm font-medium text-foreground">{s.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.reason || 'No reason recorded'} · added {fmtDate(s.createdAt)}
                    {s.createdByName ? ` by ${s.createdByName}` : ''}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRemoving(s)}
                  disabled={removeSuppression.isLoading}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {confirmingAdd ? (
        <ConfirmModal
          title="Block all billing email"
          body={
            <span>
              This immediately stops ALL billing email to <strong>{email.trim().toLowerCase()}</strong>.
              Invoice emails, reminders, and payment plan notices will not send while this block
              is active. It stays in effect until someone removes it here.
            </span>
          }
          confirmLabel="Block all billing email"
          destructive
          isSaving={addSuppression.isLoading}
          onClose={() => setConfirmingAdd(false)}
          onConfirm={handleAdd}
        />
      ) : null}

      {removing ? (
        <ConfirmModal
          title="Remove from exclusion list"
          body={
            <span>
              Billing emails to <strong>{removing.email}</strong> will resume on the normal
              cadence.
            </span>
          }
          confirmLabel="Remove block"
          isSaving={removeSuppression.isLoading}
          onClose={() => setRemoving(null)}
          onConfirm={handleRemove}
        />
      ) : null}
    </Card>
  );
};

// ---------------------------------------------------------------------------

const PaymentPlanSettingsCard = ({ settings, onSave, isSaving }) => {
  const [threshold, setThreshold] = useState(settings?.paymentPlanMinInvoiceAgeDays ?? 60);
  useEffect(() => {
    setThreshold(settings?.paymentPlanMinInvoiceAgeDays ?? 60);
  }, [settings?.paymentPlanMinInvoiceAgeDays]);

  const dirty = Number(threshold) !== settings?.paymentPlanMinInvoiceAgeDays;
  const valid = Number.isInteger(Number(threshold)) && threshold >= 0 && threshold <= 365;

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-2 text-sm text-foreground">
          Offer a payment plan when an invoice is
          <Input
            type="number"
            min={0}
            max={365}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-20"
          />
          days old
        </div>
        <p className="text-xs text-muted-foreground">
          Counted from the invoice date, not the due date. When an invoice
          reaches this age the plan unlocks on the client&apos;s billing page,
          reminder emails include the plan option, and a one-time payment plan
          email goes out if a reminder hasn&apos;t already offered it.
        </p>
        {dirty ? (
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button
              variant="secondary"
              onClick={() => setThreshold(settings?.paymentPlanMinInvoiceAgeDays ?? 60)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              disabled={isSaving || !valid}
              onClick={() =>
                onSave({
                  paymentPlanMinInvoiceAgeDays: Number(threshold),
                })
              }
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------

const BillingSettingsPage = () => {
  const { data: settings, isLoading, isError, refetch } = useFetchBillingSettings();
  const updateSettings = useUpdateBillingSettings();

  const save = async (patch) => {
    try {
      await updateSettings.mutateAsync(patch);
      toast.success('Settings saved');
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not save settings');
      return false;
    }
  };

  return (
    <PrivateLayout>
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Billing Settings</h1>
          <p className="text-sm text-muted-foreground">
            Reminder cadence, the billing email kill switch, and payment plan rules.
          </p>
        </div>

        {isError ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-10">
            <AlertTriangle className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Could not load settings.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className="divide-y divide-border border-t border-border">
              <SettingsSection
                title="Reminder emails"
                description="The master switch. Pausing stops all reminder email to every client immediately."
              >
                <MasterPauseCard
                  settings={settings}
                  isSaving={updateSettings.isLoading}
                  onToggle={(enabled) => save({ remindersEnabled: enabled })}
                />
              </SettingsSection>

              <SettingsSection
                title="Reminder cadence"
                description="When reminders go out, in days after the invoice due date."
              >
                <CadenceEditorCard
                  settings={settings}
                  isSaving={updateSettings.isLoading}
                  onSave={save}
                />
              </SettingsSection>

              <SettingsSection
                icon={<AlertTriangle className="h-4 w-4 shrink-0 text-status-error-fg" />}
                title="Email exclusion list"
                description="Emergency kill switch. Listed addresses receive NO billing email of any kind until removed here."
              >
                <ExclusionListCard />
              </SettingsSection>

              <SettingsSection
                title="Payment plans"
                description="How old an invoice must be (days since it was issued, not since it was due) before clients see the payment plan option. Staff can still enable one early."
              >
                <PaymentPlanSettingsCard
                  settings={settings}
                  isSaving={updateSettings.isLoading}
                  onSave={save}
                />
              </SettingsSection>
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
};

export default BillingSettingsPage;
